"use client";

import React from "react";
import PlatformPicker from "@/components/creator/PlatformPicker";
import { PLATFORMS } from "@/app/constants/creatorNetwork";
import { EMPTY_SOCIAL_PROFILE } from "@/types/creatorNetwork";
import type { CreatorApplication, SocialProfile } from "@/types/creatorNetwork";

type StepProps = {
  data: CreatorApplication;
  onUpdate: (patch: Partial<CreatorApplication>) => void;
  errors: Record<string, string | null | undefined>;
};

export default function StepSocials({ data, onUpdate, errors }: StepProps) {
  const profiles = data.socialProfiles;
  const selectedPlatforms = profiles.map((p) => p.platform);

  const togglePlatform = (value: string) => {
    if (selectedPlatforms.includes(value)) {
      onUpdate({ socialProfiles: profiles.filter((p) => p.platform !== value) });
    } else {
      onUpdate({
        socialProfiles: [...profiles, { ...EMPTY_SOCIAL_PROFILE, platform: value }],
      });
    }
  };

  const updateProfile = (index: number, patch: Partial<SocialProfile>) => {
    onUpdate({
      socialProfiles: profiles.map((p, i) => (i === index ? { ...p, ...patch } : p)),
    });
  };

  const removeProfile = (index: number) => {
    onUpdate({ socialProfiles: profiles.filter((_, i) => i !== index) });
  };

  return (
    <div>
      <h3 className="step-title text-white text-2xl font-bold mb-1">Your socials</h3>
      <p className="text-white/60 mb-6">Select all the platforms that you create on.</p>

      <div className="border border-white/15 bg-white/[0.03] rounded-2xl p-5">
        <span className="block text-white font-semibold mb-3 sm:px-4">Platform</span>
        <div className="flex flex-wrap gap-2.5 sm:px-4" role="group" aria-label="Platform">
          {PLATFORMS.map((p) => {
            const active = selectedPlatforms.includes(p.value);
            return (
              <button
                key={p.value}
                type="button"
                aria-pressed={active}
                onClick={() => togglePlatform(p.value)}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-150 ${
                  active
                    ? "bg-green border-green text-darkGrey"
                    : "border-white/30 text-white/80 hover:border-white/60"
                }`}
              >
                {p.name}
              </button>
            );
          })}
        </div>

        {profiles.length === 0 && (
          <p className="mt-4 text-white/50 text-sm sm:px-4">
            Choose the platforms above to continue.
          </p>
        )}

        {profiles.map((profile, i) => (
          <PlatformPicker
            key={profile.platform}
            profile={profile}
            index={i}
            isPrimary={i === 0}
            onChange={(patch) => updateProfile(i, patch)}
            onRemove={() => removeProfile(i)}
            errors={i === 0 ? errors : {}}
          />
        ))}
      </div>

      {errors.socials && (
        <div className="mt-3 text-red text-sm sm:px-4">{errors.socials}</div>
      )}
    </div>
  );
}
